import { capitalizeString } from 'lib/helpers'
import {
  BlurbObject,
  PAINTING_SEO_BLURBS_BY_PATH,
  PHOTOGRAPHY_SEO_BLURBS_BY_PATH,
  SCULPTURE_SEO_BLURBS_BY_PATH,
} from './data'
import { ConnectorProps as SeoConnectorProps } from '../../connectors/SeoContent'

type Tile = {
  url: string
  name: string
  alt_text: string
  image_url: string
}

type UrlObject = {
  name: string
  url: string
}

type RowTiles = {
  title: string
  tiles: ReadonlyArray<Tile>
  choices?: ReadonlyArray<UrlObject>
  text_links?: ReadonlyArray<{
    name: string
    links: ReadonlyArray<UrlObject>
  }>
}

type MergedTiles = {
  title: string
  tiles: Tile[]
}

const SPECIAL_SEO_BLURB_CATEGORIES = {
  PAINTINGS: 'Paintings',
  SCULPTURE: 'Sculpture',
  PHOTOGRAPHY: 'Photography',
}

export const getBlurb = (params: Record<string, any>): string => {
  const { hasPrints, totalHits, search, category, style, subject, size } = params

  if (!totalHits) {
    return ''
  }

  const capitalizedSize = capitalizeString(size)

  if (hasPrints) {
    return (
      `Saatchi Art offers ${totalHits.toLocaleString()} high quality open edition prints suitable for any occasion. ` +
      `Our ${capitalizedSize} ${style} ${subject} prints are available in a variety of sizes and frame selections ` +
      `so that you can customize to suit your room.`
    )
  }

  switch (category) {
    case 'All':
      return `Discover an unparalleled collection of over ${totalHits.toLocaleString()} original artworks at Saatchi Art — a global online gallery empowering the creative vision of artists from around the world. Explore an extensive selection of paintings, photography, sculptures, drawings, collages, and mixed media artworks.`

    case 'Drawings':
      return (
        `Choose from a wide-ranging selection of ${totalHits.toLocaleString()} original ${capitalizedSize} ${style} ${subject} drawings and ` +
        `prints ranging in subject, style and media from figurative to abstract, monochromatic to ` +
        `multicoloured, and charcoal to pencil.`
      )

    case 'Collage':
      return (
        `With traditional, photomontage, decoupage and digital mixed media works, Saatchi Art offers ` +
        `${totalHits.toLocaleString()} of two and three dimensional ${capitalizedSize} ${style} ${subject} collages so you can ` +
        `find the perfect piece for your space.`
      )

    case 'Mixed Media':
      return (
        `Saatchi Art is proud to offer ${totalHits.toLocaleString()} innovative ${capitalizedSize} ${style} ${subject} mixed media ` +
        `artworks, digitally crafted to make any space uniquely yours.`
      )

    case 'Digital':
      return (
        `As the world's leading online art gallery, Saatchi Art helps art lovers discover and buy ` +
        `${totalHits.toLocaleString()} ${capitalizedSize} ${style} ${subject} digital artworks through ` +
        `our best-in-class curation team and unparalleled roster of international emerging artists.`
      )

    case 'Printmaking':
      return (
        `With ${totalHits.toLocaleString()} ${capitalizedSize} ${style} ${subject} printmaking artworks available, Saatchi Art is ` +
        `home to popular techniques such as relief, intaglio, lithography, and screenprinting.`
      )

    default:
      return ''
  }
}

export const getPaintingsSEOBlurb = ({
  urlPath,
}: {
  urlPath: string
}): ReadonlyArray<BlurbObject> => {
  return PAINTING_SEO_BLURBS_BY_PATH[urlPath] ?? null
}

export const getPhotographySEOBlurb = ({
  urlPath,
}: {
  urlPath: string
}): ReadonlyArray<BlurbObject> => {
  return PHOTOGRAPHY_SEO_BLURBS_BY_PATH[urlPath] ?? null
}

export const getSculptureSEOBlurb = ({
  urlPath,
}: {
  urlPath: string
}): ReadonlyArray<BlurbObject> => {
  return SCULPTURE_SEO_BLURBS_BY_PATH[urlPath] ?? null
}

export const getBlurbLength = (blurb: ReadonlyArray<BlurbObject>): number => {
  return blurb.reduce((acc, { title, content }) => (acc += title.length + content.length), 0)
}

export const getNonTopNavBlurb = (props: SeoConnectorProps) => {
  if (Object.values(SPECIAL_SEO_BLURB_CATEGORIES).includes(props.category)) {
    return getSpecialSEOBlurb(props)
  }

  const { metadata } = props
  const initialContent = getBlurb(props)
  const initialBlurb = !!initialContent
    ? [
        {
          title: '',
          content: initialContent,
        },
      ]
    : []
  const introduction = metadata.intro
    ? [
        {
          title: '',
          content: metadata.intro,
        },
      ]
    : []
  const sections = metadata.sections
    .map((item) => {
      const link = metadata[`section_header_${item}`]
      const description = metadata[`section_copy_${item}`]

      if (link && description && item !== 'related_searches') {
        return {
          title: link,
          content: description,
        }
      } else {
        return undefined
      }
    })
    .filter((items) => !!items)

  return [...initialBlurb, ...introduction, ...sections]
}

export const mergeTiles = (tilesArray: ReadonlyArray<RowTiles>) => {
  const result: ReadonlyArray<MergedTiles> = [
    {
      title: 'Styles & Subjects',
      tiles: [],
    },
    {
      title: 'Mediums & Materials',
      tiles: [],
    },
  ]

  tilesArray.forEach(({ title, tiles }) => {
    if (title.toLowerCase().includes('style') || title.toLowerCase().includes('subject')) {
      result[0].tiles.push(...tiles)
    } else if (title.toLowerCase().includes('medium') || title.toLowerCase().includes('material')) {
      result[1].tiles.push(...tiles)
    }
  })

  return result
}

const getSpecialSEOBlurb = ({ category, title }) => {
  const capitalizeTitle = title.replace(' For Sale', '') // just remove For Sale from the H1 of the page to fit in this SEO
  const lowerCaseTitle = capitalizeTitle.toLowerCase()
  const firstLetterUpperCaseTitle = lowerCaseTitle.charAt(0).toUpperCase() + lowerCaseTitle.slice(1)

  switch (category) {
    case SPECIAL_SEO_BLURB_CATEGORIES.PAINTINGS:
      return [
        {
          title: `${capitalizeTitle} for Sale`,
          content: `Discover Saatchi Art’s ${lowerCaseTitle}. Saatchi Art offers a diverse collection of ${lowerCaseTitle} for sale by emerging artists around the world. Our selection includes a variety of styles, mediums, and techniques to suit any space or taste. Whether you're drawn to bold contemporary abstracts, serene landscapes, or evocative portraits, you’ll find original paintings that bring the joy of art to your home or collection.`,
        },
        {
          title: `Why Choose ${capitalizeTitle}`,
          content:
            'Original paintings offer more than visual appeal.Each piece is a one-of-a-kind creation that adds authenticity, personality, and artistic expression to any space. Unlike mass-produced prints, original paintings showcase the artist’s hand, making them meaningful additions to any space. Saatchi Art ensures authenticity by providing certificates with each purchase, giving you confidence in your investment. Plus, purchasing original art supports artists, fostering creativity and artistic innovation.',
        },
        {
          title: 'Find Your Perfect Piece with Saatchi Art',
          content:
            "Discovering the right painting is effortless with Saatchi Art. Our intuitive filters let you explore by style, size, color, and budget, helping you find the perfect piece to match your vision. Whether you're searching for a striking statement or a finishing touch, our global selection of fine art paintings offers endless inspiration. Transform your space with original, high-quality art from Saatchi Art. Start browsing today to find a painting that speaks to you.",
        },
      ]

    case SPECIAL_SEO_BLURB_CATEGORIES.SCULPTURE:
      return [
        {
          title: `The Art of ${capitalizeTitle}: Timeless Creations`,
          content: `At Saatchi Art, ${lowerCaseTitle} takes countless forms, brought to life by talented artists worldwide. Whether crafted in bronze, marble, wood, or contemporary materials like steel and resin, sculptures hold a unique power to transform spaces. From finely detailed figurative pieces to dynamic abstract designs, these works make compelling additions to homes, gardens, or outdoor spaces.`,
        },
        {
          title: 'A Journey Through Time and Form',
          content:
            'Sculpture is one of the most enduring art forms, with a history stretching back to ancient civilizations. Over time, techniques such as carving, modeling, and casting have evolved, allowing artists to experiment with new materials and creative expressions. In the hands of up-and-coming sculptors, the medium continues to evolve with a reverence for tradition and innovation.',
        },
        {
          title: `${capitalizeTitle}: A Monumental Impact`,
          content: `${firstLetterUpperCaseTitle}s commands attention for a strong visual presence in any setting. An intimate tabletop object, a dynamic wall-mounted relief, or a monumental outdoor installation adds depth and dimensionality to their surroundings. Materials like stone, wood, metal, and glass lend distinct textures and character. . From figurative representations to abstract interpretations, each sculpture tells its story through form, technique, and distinctive artistic vision.`,
        },
        {
          title: `Discover One-of-a-Kind ${capitalizeTitle}s at Saatchi Art`,
          content: `Saatchi Art features a wide range of original sculptures, showcasing both emerging and established artists. Whether you’re drawn to traditional and timeless styles or the cutting-edge and contemporary, you’ll find pieces that speak to you. Explore our curated selection of ${lowerCaseTitle}s to transform your space with the power of three-dimensional art.`,
        },
      ]

    case SPECIAL_SEO_BLURB_CATEGORIES.PHOTOGRAPHY:
      return [
        {
          title: `Discover ${capitalizeTitle}`,
          content: `Explore a selection of ${lowerCaseTitle}, featuring captivating images created by talented photographers from around the world. Whether you’re inspired by sweeping natural vistas, narrative portraits, or thought-provoking abstract compositions, photography has a remarkable ability to shift perspectives and stir emotions.  The breadth of works on Saatchi Art makes finding a piece that aligns with your taste easy.`,
        },
        {
          title: `Why Choose ${capitalizeTitle}?`,
          content: `${firstLetterUpperCaseTitle} bridges reality and artistic expression, offering fresh perspectives on the familiar. A timeless black-and-white portrait might evoke deep nostalgia, while a vibrant urban skyline can infuse a room with energy. Its versatility ensures it fits beautifully into any setting—be it a modern minimalist space or a bold, eclectic interior. For collectors, ${lowerCaseTitle} represents a medium that constantly pushes boundaries. Many contemporary artists experiment with innovative techniques like digital manipulation and mixed media, redefining what photography can achieve. This dynamic art form appeals to those who appreciate creativity that evolves with the times. Photographic works are available in various formats, from affordable open editions to exclusive one-of-a-kind prints, making them an  excellent option for new or seasoned collectors.`,
        },
        {
          title: `Explore ${capitalizeTitle} Art for Your Space`,
          content: `Spanning a wide range of ${lowerCaseTitle} options, whether you’re looking to complement your decor, connect with a piece on a deeper level, our diverse selection ensures there is something for everyone. Use intuitive filters to browse by subject, mood, or style, and uncover photography that speaks to you. Start your journey today and bring home artwork that captures unforgettable moments, tells a unique story, or simply inspires awe.`,
        },
      ]
  }
}
